import React, {ReactNode} from 'react';
import {
    Button,
    Flex,
    Icon,
    Link,
    Popover,
    PopoverPlacement,
    PriorityIcon,
    Title
} from '@priority-software/priority-style-react';
import {isRTL} from '../../../Localization';
import texts from '../texts';
import {HyperlinkText} from '../../../components/Common/LinkTexts/LinkTexts';
import {returnFocus} from '../../../_shared/GWTBridge/invoke/genPage';
import {
    getTopDialogZindex,
    setIsCloseKeysBlocked
} from '../../../_shared/GWTBridge/invoke/priModalDlg';
import styles from '../styles/helpPopover.module.scss';
import keyCodes, {keys} from '../../../constants/KeyCodes';
import {showFormHelp} from '../actions/helpActions';
import {sendMonitor} from '../../../utils/monitoring';

type SkinProps = 'default' | 'primary';

enum TitleSkins {
    h2 = 'h2',
    h3 = 'h3',
    h4 = 'h4',
    h5 = 'h5'
}
type TitleSkin = TitleSkins;

interface HelpPopoverState {
    open: boolean;
    anchor: HTMLElement;
    title: ReactNode;
    content: string | ReactNode;
    onLinkClick?: (e: any | any[]) => void;
    linkData?: any[];
    onClose?: () => void;
    usePortal?: boolean;
    outsideClickClose?: boolean;
    skin?: SkinProps;
    portalSelector?: string | HTMLElement;
    withFooter?: boolean;
    placement?: PopoverPlacement;
    titleSkin?: TitleSkin;
}

const arrowModifier = [
    {
        name: 'flip',
        options: {
            fallbackPlacements: isRTL()
                ? ['top-start', 'bottom-start', 'right', 'right-start']
                : ['top-end', 'bottom-end', 'left', 'left-start']
        }
    }
];

export class HelpPopover extends React.Component<{}, HelpPopoverState> {
    defaultState = {
        open: false,
        title: null,
        content: null,
        anchor: null,
        onLinkClick: null,
        linkData: null,
        onClose: null,
        usePortal: false,
        outsideClickClose: true,
        skin: 'default' as SkinProps,
        portalSelector: undefined,
        placement: undefined,
        titleSkin: TitleSkins.h3,
        withFooter: true
    };
    state = {
        ...this.defaultState
    };

    showHelpPopover = (params: Partial<HelpPopoverState & {linkList: any[]}>) => {
        const {
            title = '',
            content,
            onLinkClick,
            anchor,
            onClose,
            outsideClickClose = true,
            usePortal,
            skin,
            portalSelector,
            placement,
            withFooter = true,
            linkList,
            ...rest
        } = params;

        window.addEventListener('keydown', this.handleKeyDown, true);

        this.setState({
            open: true,
            anchor,
            title,
            content,
            linkData: linkList,
            onLinkClick,
            onClose,
            outsideClickClose,
            usePortal,
            skin,
            placement,
            portalSelector,
            withFooter,
            ...rest
        });

        setIsCloseKeysBlocked(true);
    };

    handleKeyDown = e => {
        if (e.key === keys.ESC || e.key === keys.ENTER) {
            e.stopImmediatePropagation();
            e.preventDefault();
        }
        e.key !== keys.CTRL && e.keyCode !== keyCodes.C && this.handleClose();
    };
    onClose = () => {
        this.handleClose();
    };

    handleClose = () => {
        if (this.state.open) {
            window.removeEventListener('keydown', this.handleKeyDown, true);
            this.state.onClose?.();
            this.setState({...this.defaultState});
            setIsCloseKeysBlocked(false);
            returnFocus();
        }
    };

    renderTitle = () => {
        return (
            <Flex justifyContent="flex-start" style={{gap: 16}}>
                <div className={styles.iconWrap}>
                    <Icon
                        componentClasses={styles.icon}
                        iconColor="primary-500"
                        icon={PriorityIcon.questionCircle}
                    />
                </div>
                {this.state.title ? (
                    <Title textColor="primary-700" fontWeight="normal" skin={this.state.titleSkin}>
                        {this.state.title}
                    </Title>
                ) : null}
            </Flex>
        );
    };
    handleLinkClick = linkData => {
        const {onLinkClick} = this.state;
        onLinkClick && linkData && onLinkClick(linkData);
        this.handleClose();
    };
    renderContent = () => {
        const {content, linkData} = this.state;
        return typeof content === 'string' ? (
            <HyperlinkText text={content} linkData={linkData} onLinkClick={this.handleLinkClick} />
        ) : (
            content
        );
    };

    isFormField = () => {
        const {anchor} = this.state;
        //Condition for new API helper in Form Field
        if (anchor?.dataset?.anchor === 'formField') return true;
        //Condition for new API helper in Context menu Grid
        if (anchor?.dataset?.forminput === 'form-input') return true;

        return (
            anchor?.tagName?.toLowerCase() === 'input' &&
            (anchor?.className?.includes('priCurrentFieldStyle') ||
                anchor?.className?.includes('priFieldBoolStyle'))
        );
    };
    renderFooter = () => {
        const isFormField = this.isFormField();
        return (
            <Flex
                justifyContent={isFormField ? 'space-between' : 'flex-end'}
                componentClasses={styles.footerWrapper}
            >
                {this.isFormField() && (
                    <Link
                        componentClasses={styles.textLink}
                        skin="primary"
                        textSize="medium"
                        onClick={() => {
                            this.onClose();
                            showFormHelp();
                            sendMonitor('form help - form field help button');
                        }}
                    >
                        {texts.formHelp}
                    </Link>
                )}
                <Button autoFocus onClick={() => this.onClose()} size="small">
                    {texts.ok}
                </Button>
            </Flex>
        );
    };

    render() {
        const {
            open,
            anchor,
            usePortal,
            outsideClickClose,
            skin,
            portalSelector,
            placement = PopoverPlacement.right
        } = this.state;

        if (!anchor) {
            return null;
        }

        return (
            <Popover
                portalSelector={portalSelector}
                skin={skin}
                usePortal={usePortal}
                outsideClickClose={outsideClickClose}
                show={open}
                footer={this.state.withFooter && this.renderFooter}
                content={this.renderContent()}
                anchor={anchor}
                popoverComponentClass={`${styles.content} ignoreMenuClickoutside ignore-react-onclickoutside`}
                title={this.renderTitle()}
                rtl={isRTL()}
                placement={placement}
                zIndex={getTopDialogZindex() + 1}
                escClosable
                hoverable={false}
                onClose={this.onClose}
                contentMaxHight={400}
                customModifiers={arrowModifier}
            />
        );
    }
}
